import { h, FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LanguageToggle, UpliftIcons } from 'up-ops-web-shared-components/lib';
import ProcessingView from '~src/domains/common/components/ProcessingView';
import * as styles from '~src/styles/domains/common/components/wrapper.module.scss';
import { HIDE_WRAPPER_PAGES } from '~src/utils/constants';
import { changeLocale } from '~src/utils/helpers';
import { AlertExitIcon } from '../assets/svg';
import { Page } from '../enums/api';
import { Country, Language, Locale } from '../enums/localization';
import { setShowBanner, setTooltipContainerRef } from '../state/commonReducer';
import {
  brandSelector,
  documentSelector,
  isProcessingSelector,
  localeSelector,
  pageSelector,
  showBannerSelector,
} from '../state/commonSelectors';
import DocumentPrint from './DocumentPrint';
import LinkWrapper from './LinkWrapper';
import Logo from './Logo';
import { Brand } from '../enums/common';

type WrapperProps = {
  children?: React.ReactNode;
};

const DismissibleBanner: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return (
    <div className={styles['wrapper-dismissible-banner']}>
      <div className={styles['wrapper-dismissible-banner-exit']}>
        <AlertExitIcon />
      </div>
      <div className={styles['wrapper-dismissible-banner-text']}>
        <div className={styles['wrapper-alert']}>
          <div className={styles['wrapper-alert-icon']}>!</div>
        </div>
        <div>{t('common:wrapper.agent-updated.message')}</div>
      </div>
      <div className={styles['wrapper-dismissible-banner-dismiss']}>
        <LinkWrapper
          id='dismiss'
          trackingConfig={{
            label: t('common:wrapper.agent-updated.dismiss'),
          }}
          onClickHandler={() => {
            dispatch(setShowBanner(false));
          }}
        >
          {t('common:wrapper.agent-updated.dismiss').toUpperCase()}
        </LinkWrapper>
      </div>
    </div>
  );
};

const Wrapper: FunctionComponent<WrapperProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const Container = useRef(null);

  const currLocale = useSelector(localeSelector);
  const page = useSelector(pageSelector);
  const isProcessing = useSelector(isProcessingSelector);
  const document = useSelector(documentSelector);
  const brand = useSelector(brandSelector);
  const wrapperClass =
    brand == Brand.UPLIFT ? styles['wrapper'] : `${styles['wrapper']} flexpay`;

  const showBanner =
    !isProcessing && page !== Page.RECONFIRM && useSelector(showBannerSelector);

  const [_, country] = currLocale.split('-');
  const showWrapper = !HIDE_WRAPPER_PAGES[page];
  const showFooter =
    country === Country.CA ||
    page == Page.ADVERSE ||
    page == Page.CREDIT_FREEZE ||
    page == Page.ERROR ||
    page == Page.INVALID_ADDRESS ||
    page == Page.NOI ||
    page == Page.OFFER_EXPIRED ||
    page == Page.OUT_OF_FILTER ||
    page === Page.PAY ||
    page == Page.REAPPLY ||
    page == Page.TRIP_EXPIRED;
  const showLanguageToggle = country === Country.CA;
  const showDocHeader = page === Page.LOAN_CONSENT || document.show;

  /** Sets locale on language toggle. */
  const onLanguageToggle = (language: string) => {
    dispatch(
      changeLocale(`${language.toLowerCase()}-${country}` as Locale, i18n),
    );
  };

  const header = showDocHeader ? (
    <div className={styles['wrapper-header']}>
      <DocumentPrint />
      <span className={styles['wrapper-header-logo']}>
        <span>{t('common:wrapper.header.powered-by')}</span>
        <Logo />
      </span>
    </div>
  ) : (
    <div className={styles['wrapper-header']}>
      <div
        data-testid='wrapper-header'
        className={styles['wrapper-header-text']}
      >
        <span>{t('common:wrapper.header.book-now')}</span>
        <span>{t('common:wrapper.header.pay')}</span>
      </div>
      <span className={styles['wrapper-header-logo']}>
        <span className={styles['wrapper-header-logo-powered-by']}>
          {t('common:wrapper.header.powered-by')}
        </span>
        <Logo />
      </span>
    </div>
  );

  useEffect(() => {
    dispatch(setTooltipContainerRef(Container.current));
  }, [Container]);

  // Note: Adding inline style because secure forms does not work properly if the children keep re-rendering
  return showWrapper ? (
    <div className={wrapperClass} ref={Container}>
      {header}
      {isProcessing && <ProcessingView />}
      {showBanner && <DismissibleBanner />}
      <div
        className={styles.padder}
        aria-hidden={isProcessing ? 'true' : 'false'}
        style={{
          display: isProcessing ? 'none' : 'flex',
          visibility: isProcessing ? 'hidden' : 'visible',
        }}
      >
        {children}
      </div>
      {showFooter && !isProcessing && (
        <div data-testid='wrapper-footer' className={styles['wrapper-footer']}>
          {showLanguageToggle ? (
            <div data-auto='LanguageToggleContainer'>
              <LanguageToggle
                displayLocale={false}
                languages={{
                  [Language.EN]: 'English',
                  [Language.FR]: 'Français',
                }}
                onSelect={onLanguageToggle}
              />
            </div>
          ) : (
            <div className={styles['footer-header']} data-auto='footerHeader'>
              <Trans
                t={t}
                i18nKey={`adverse:${brand.toLowerCase()}.questions`}
              />
            </div>
          )}
          <div
            className={styles['wrapper-footer-contact']}
            data-auto='contactContainer'
          >
            <div
              className={styles['wrapper-contact']}
              data-auto='footerPhoneContainer'
            >
              <UpliftIcons.Phone className={styles.icons} />
              <a
                target='_blank'
                rel='noopener'
                href={`tel:${t('common:wrapper.footer.telephone.link')}`}
                data-auto='footerPhoneLink'
              >
                {t('common:wrapper.footer.telephone.text')}
              </a>
            </div>
            <div
              className={styles['wrapper-contact']}
              data-auto='footerEmailContainer'
            >
              <UpliftIcons.Envelope className={styles.icons} />
              <a
                target='_blank'
                rel='noopener'
                href={`mailto:${t(
                  `common:wrapper.footer.${brand.toLowerCase()}.email`,
                )}`}
                data-auto='footerEmailLink'
              >
                {t(`common:wrapper.footer.${brand.toLowerCase()}.email`)}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div
      className={wrapperClass}
      ref={Container}
      aria-hidden={isProcessing ? 'true' : 'false'}
      style={{
        display: isProcessing ? 'none' : 'flex',
        visibility: isProcessing ? 'hidden' : 'visible',
      }}
    >
      {children}
    </div>
  );
};

export default Wrapper;
